import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  Minus,
  Receipt,
  ShoppingCart,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import type { DashboardSummary } from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationFilter, useWorkingLocation } from '../../lib/locationContext';
import { count, money, moneyCompact } from '../../lib/format';
import { Card, CardHeader, ErrorState, Spinner, cn } from '../../components/ui';
import { CurrencyNotice } from '../../components/CurrencyNotice';
import { PageHeader } from '../../layouts/AppShell';
import { LocationScope } from '../../components/LocationScope';
import { GrowthCard } from './GrowthCard';

export function DashboardPage(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();

  // Same omission as the growth card: the endpoint narrows by location, the
  // page never asked it to.
  const { isReady } = useWorkingLocation();
  const scope = useLocationFilter();

  /*
    Waits for the working location to settle before asking.

    `branchId` is null until the permitted list arrives and a choice is made
    from it, so fetching sooner asks for the whole business and then re-asks
    for one location — an owner whose saved choice is a single shop watches
    every location's money flash past before the figures they wanted. Briefly
    empty is honest; briefly wrong is not. The lists have gated on this for a
    while; the dashboard did not.
  */
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['dashboard', business?.businessId, scope],
    queryFn: () =>
      api.get<DashboardSummary>(path(`/dashboard${qs(scope)}`)),
    enabled: Boolean(business) && isReady,
  });

  if (isPending) return <Spinner label="Loading your dashboard" />;
  if (error) return <ErrorState message="Could not load the dashboard." onRetry={() => refetch()} />;

  const currency = data.currencyCode;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`Today, ${new Date(`${data.date}T00:00:00`).toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}`}
      />

      {/*
        Which locations these tiles cover.

        The tiles narrow by the working location — see the query above — and
        said nothing about it, so a day with fifteen deliveries at two other
        locations read as ₦0 and 0 transactions with no way to tell that from
        a quiet day or from lost data. It was reported as exactly that. The
        growth card below has carried this line since the same thing happened
        to it; both now use one component.
      */}
      <div className="mb-4">
        <LocationScope testId="dashboard-scope" />
      </div>

      {/* No switcher: the dashboard has no currency parameter to switch with,
          and "today, in one currency" is the question it answers. A business
          trading in two goes to Reports, which does. */}
      {data.currency.isMixed && (
        <div className="mb-4">
          <CurrencyNotice currency={data.currency} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatTile
          label="Today's sales"
          value={moneyCompact(data.today.sales, currency)}
          exact={money(data.today.sales, currency)}
          delta={delta(data.today.sales, data.yesterday.sales)}
          tone="income"
          icon={<TrendingUp className="size-4" />}
        />
        <StatTile
          label="Today's expenses"
          value={moneyCompact(data.today.expenses, currency)}
          exact={money(data.today.expenses, currency)}
          delta={delta(data.today.expenses, data.yesterday.expenses)}
          // More spending than yesterday is not good news, so the delta arrow
          // is inverted for this tile.
          invertDelta
          tone="outflow"
          icon={<Receipt className="size-4" />}
        />
        <StatTile
          label="Today's profit"
          value={moneyCompact(data.today.profit, currency)}
          exact={money(data.today.profit, currency)}
          delta={delta(data.today.profit, data.yesterday.profit)}
          tone={Number(data.today.profit) < 0 ? 'outflow' : 'income'}
          icon={<Wallet className="size-4" />}
        />
        <StatTile
          label="Transactions"
          value={count(data.today.transactionCount)}
          delta={delta(
            String(data.today.transactionCount),
            String(data.yesterday.transactionCount),
          )}
          tone="neutral"
          icon={<ShoppingCart className="size-4" />}
        />
        <StatTile
          label="Outstanding"
          value={moneyCompact(data.today.outstandingPayments, currency)}
          exact={money(data.today.outstandingPayments, currency)}
          footnote={
            data.today.outstandingCount > 0
              ? `${count(data.today.outstandingCount)} unpaid ${
                  data.today.outstandingCount === 1 ? 'sale' : 'sales'
                }`
              : 'Everything settled'
          }
          tone="owed"
          icon={<Clock className="size-4" />}
          href="/sales?outstandingOnly=true"
        />
      </div>

      {/*
        Revenue, and the throughput it came from, kept visibly apart.

        A ₦3,000 ride on which the rider keeps ₦2,500 earns ₦500. The
        top row is what the business earned; the row below it is what passed
        through to earn it. They are never added, and the rule between them
        says so.
      */}
      <section className="mt-6">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold text-slate-900">Revenue today</h2>
          <p className="text-xs text-slate-500">
            {money(data.revenue.totalRevenue, currency)} earned
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label="Total revenue"
            value={moneyCompact(data.revenue.totalRevenue, currency)}
            exact={money(data.revenue.totalRevenue, currency)}
            delta={delta(data.revenue.totalRevenue, data.previousRevenue.totalRevenue)}
            tone="income"
            icon={<TrendingUp className="size-4" />}
          />
          <StatTile
            label="Shop revenue"
            value={moneyCompact(data.revenue.shopRevenue, currency)}
            exact={money(data.revenue.shopRevenue, currency)}
            delta={delta(data.revenue.shopRevenue, data.previousRevenue.shopRevenue)}
            footnote="Sales that are not a delivery"
            tone="neutral"
            icon={<ShoppingCart className="size-4" />}
          />
          <StatTile
            label="Delivery commission"
            value={moneyCompact(data.revenue.deliveryCommission, currency)}
            exact={money(data.revenue.deliveryCommission, currency)}
            delta={delta(data.revenue.deliveryCommission, data.previousRevenue.deliveryCommission)}
            footnote="The company's share of each ride"
            tone="neutral"
            icon={<Wallet className="size-4" />}
          />
          <StatTile
            label="Rider remittance"
            value={moneyCompact(data.revenue.riderRemittanceRevenue, currency)}
            exact={money(data.revenue.riderRemittanceRevenue, currency)}
            footnote="The agreed weekly charge"
            tone="neutral"
            icon={<Receipt className="size-4" />}
          />
        </div>

        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <p className="mb-2 text-xs font-medium text-slate-500">
            Scale and operations — how much business {business?.businessName ?? 'this business'} handled,
            not what it earned
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-5">
            {([
              ['Gross delivery value', money(data.revenue.grossDeliveryValue, currency)],
              ['Rider payouts', money(data.revenue.riderPayouts, currency)],
              ['Company kept', money(data.revenue.companyKept, currency)],
              ['Deliveries', count(data.revenue.deliveries)],
              ['Customers', count(data.revenue.customers)],
            ] as const).map(([label, v]) => (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <dt className="text-slate-600">{label}</dt>
                <dd className="font-medium tabular-nums text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <GrowthCard />

      <Card className="mt-6">
        <CardHeader
          title="Last 14 days"
          subtitle="Sales against expenses, in your business timezone"
        />
        <TrendChart data={data} />
      </Card>
    </>
  );
}

// ── Stat tile ───────────────────────────────────────────────────────────────

type Tone = 'income' | 'outflow' | 'owed' | 'neutral';

const TONE_STYLES: Record<Tone, { icon: string; value: string }> = {
  income: { icon: 'bg-emerald-50 text-emerald-600', value: 'text-slate-900' },
  outflow: { icon: 'bg-red-50 text-red-600', value: 'text-slate-900' },
  owed: { icon: 'bg-amber-50 text-amber-600', value: 'text-slate-900' },
  neutral: { icon: 'bg-slate-100 text-slate-600', value: 'text-slate-900' },
};

interface Delta {
  percent: number;
  direction: 'up' | 'down' | 'flat';
}

/**
 * Percentage change against yesterday.
 *
 * Returns null when yesterday was zero: "+∞%" is not information, and showing
 * "+100%" for a first sale would be actively misleading.
 */
function delta(today: string, yesterday: string): Delta | null {
  const current = Number(today);
  const previous = Number(yesterday);

  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;

  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(change) < 0.05) return { percent: 0, direction: 'flat' };

  return {
    percent: Math.abs(Math.round(change * 10) / 10),
    direction: change > 0 ? 'up' : 'down',
  };
}

function StatTile({
  label,
  value,
  exact,
  delta: change,
  invertDelta,
  footnote,
  tone,
  icon,
  href,
}: {
  label: string;
  value: string;
  exact?: string;
  delta?: Delta | null;
  invertDelta?: boolean;
  footnote?: string;
  tone: Tone;
  icon: ReactNode;
  href?: string;
}): ReactNode {
  const styles = TONE_STYLES[tone];

  // "Good" means up for income, down for expenses.
  const isGood =
    change?.direction === 'flat'
      ? null
      : change
        ? invertDelta
          ? change.direction === 'down'
          : change.direction === 'up'
        : null;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-600">{label}</p>
        <span className={cn('flex size-8 items-center justify-center rounded-lg', styles.icon)}>
          {icon}
        </span>
      </div>

      <p className={cn('mt-3 text-2xl font-semibold tabular', styles.value)} title={exact}>
        {value}
      </p>

      {change && (
        <p
          className={cn(
            'mt-1.5 flex items-center gap-1 text-xs font-medium',
            isGood === null ? 'text-slate-500' : isGood ? 'text-emerald-600' : 'text-red-600',
          )}
        >
          {change.direction === 'flat' ? (
            <Minus className="size-3.5" aria-hidden />
          ) : change.direction === 'up' ? (
            <ArrowUpRight className="size-3.5" aria-hidden />
          ) : (
            <ArrowDownRight className="size-3.5" aria-hidden />
          )}
          {change.direction === 'flat' ? 'No change' : `${change.percent}%`}
          <span className="font-normal text-slate-500">vs yesterday</span>
        </p>
      )}

      {footnote && !change && <p className="mt-1.5 text-xs text-slate-500">{footnote}</p>}
    </>
  );

  if (href) {
    return (
      <Link to={href} className="card p-4 transition-shadow hover:shadow-md sm:p-5">
        {body}
      </Link>
    );
  }
  return <div className="card p-4 sm:p-5">{body}</div>;
}

// ── Trend chart ─────────────────────────────────────────────────────────────

function TrendChart({ data }: { data: DashboardSummary }): ReactNode {
  const series = data.trend.map((point) => ({
    date: point.date,
    label: new Date(`${point.date}T00:00:00`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    }),
    sales: Number(point.sales),
    expenses: Number(point.expenses),
  }));

  const hasActivity = series.some((point) => point.sales > 0 || point.expenses > 0);

  if (!hasActivity) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-slate-500">
          Nothing recorded in the last two weeks. Your chart appears once you record a sale.
        </p>
      </div>
    );
  }

  return (
    <div className="h-64 sm:h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
          <defs>
            <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#059669" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#059669" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dc2626" stopOpacity={0.16} />
              <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={(value: number) =>
              new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)
            }
          />
          <Tooltip
            // Recharts types the formatter value as a union including
            // undefined, so it is taken as unknown and coerced here.
            formatter={(value: unknown, name: unknown) => [
              money(Number(value).toFixed(2), data.currencyCode),
              name === 'sales' ? 'Sales' : 'Expenses',
            ]}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 12,
              boxShadow: '0 4px 12px rgb(15 23 42 / 0.08)',
            }}
          />
          <Area
            type="monotone"
            dataKey="sales"
            stroke="#059669"
            strokeWidth={2}
            fill="url(#salesFill)"
          />
          <Area
            type="monotone"
            dataKey="expenses"
            stroke="#dc2626"
            strokeWidth={2}
            fill="url(#expenseFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
